import { useCallback, useEffect } from 'react';

import { APButton, APTypography } from '@shared/components/atoms';
import APDialog from '@shared/components/molecules/dialog';
import { useTranslate } from '@shared/hooks/use-translate';
import { useAppVersionStore } from '@shared/stores/use-app-version-store';
import {
  fetchVersionManifest,
  getBuildVersion,
  resolveVersionStatus,
} from '@shared/utils/app-version';
import { env } from '@shared/utils/env';

import type { FC } from 'react';

import './styles.scss';

/** Five minutes: a new build is noticed within one coffee, and the poll is one tiny uncached GET. */
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

const noop = () => undefined;

/**
 * Polls the deployed `version.json` and locks the tab when it is below `min_version`.
 *
 * Mounted once, at the provider root, so the lock covers every screen — including the ones without
 * the default layout. The softer "new version available" line is `AppVersionBanner`, rendered by
 * the default layout under its header, and reads the same store.
 *
 * It checks on an interval, when the tab becomes visible again (a laptop reopened after a weekend is
 * the tab most likely to be behind), and when a lazy chunk fails to load (`vite:preloadError`) —
 * which is what a stale tab hits first, because the chunk it asks for no longer exists.
 *
 * The lock is deliberately inescapable: no close button, no backdrop click, Escape does nothing. A
 * `min_version` is only raised when running the old build is unsafe, so the one way out is a reload.
 */
export const AppVersionCheck: FC = () => {
  const translate = useTranslate();
  const status = useAppVersionStore((state) => state.status);
  const setResult = useAppVersionStore((state) => state.setResult);
  const current = getBuildVersion();

  const check = useCallback(async () => {
    const manifest = await fetchVersionManifest(`${env.APP_PUBLIC_FOLDER}/version.json`);
    // A failed check changes nothing: a flaky network must neither hide the banner nor lift a lock.
    if (manifest === null) return;
    setResult(resolveVersionStatus(current, manifest), manifest.version);
  }, [current, setResult]);

  useEffect(() => {
    if (current === 0) return undefined;

    void check();
    const interval = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const onPreloadError = () => void check();

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, [check, current]);

  const title = translate('appVersion.required.title', undefined, 'This version is out of date');

  return (
    <APDialog
      isOpen={status === 'update_required'}
      onClose={noop}
      closeOnBackdropClick={false}
      size="small"
      ariaLabel={title}
      ariaDescribedBy="app-version-required-body"
      data-testid="app-version-required"
    >
      <div className="app-version__required">
        <APTypography as="h2" size="large" weight="600">
          {title}
        </APTypography>
        <APTypography id="app-version-required-body" size="small" color="muted">
          {translate(
            'appVersion.required.body',
            undefined,
            'A required update was released. Refresh to keep working — anything unsaved on this screen will be lost.'
          )}
        </APTypography>
        <APButton variant="primary" fullWidth onClick={() => window.location.reload()}>
          {translate('appVersion.refresh', undefined, 'Refresh now')}
        </APButton>
      </div>
    </APDialog>
  );
};

AppVersionCheck.displayName = 'AppVersionCheck';

export default AppVersionCheck;
